import React, { useState, useEffect } from "react"

import remarkGfm from "remark-gfm"
import Markdown from "react-markdown"
import "react-day-picker/dist/style.css"
import { DayPicker } from "react-day-picker"
import { initializeApp } from "firebase/app"
import { format } from "date-fns"
import { Helpers } from "@flexday/common-react"
import { Utils } from "@flexday/common"
import { trackEvent } from "@intercom/messenger-js-sdk"
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getFirestore,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore"
import { useDocumentData } from "react-firebase-hooks/firestore"
import "./style.css"

import InvoiceItem from "./invoice-item"
import Button from "../reusable-components/button"
import ClockIcon from "../assets/icons/clock.png"
import {
  formatCurrency,
  formatCurrencyCompact,
  formatDiscount,
} from "./formatters"
import { spacesOptions } from "../utils/constants"

const BookingConfigurator = ({ inventoryPageId, currency }) => {
  const MIN_DISCOUNT_DAYS = 3
  const [dates, setDates] = useState([])
  const [loading, setLoading] = useState(false)
  const [inventory, setInventory] = useState({})
  const [isCartEmpty, setIsCartEmpty] = useState(true)
  const cartId = window.localStorage.getItem("cartId")

  const { getFirebaseConfig } = Helpers
  const firebaseConfig = getFirebaseConfig({
    REACT_APP_FIREBASE_API_KEY: process.env.REACT_APP_FIREBASE_API_KEY,
    REACT_APP_FIREBASE_APP_ID: process.env.REACT_APP_FIREBASE_APP_ID,
    REACT_APP_FIREBASE_MESSAGE_SENDER_ID:
      process.env.REACT_APP_FIREBASE_MESSAGE_SENDER_ID,
    REACT_APP_FIREBASE_PROJECT_ID: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  })

  const app = initializeApp(firebaseConfig)
  const firestore = getFirestore(app)

  useEffect(() => {
    async function getCart() {
      const snapshot = await getDoc(doc(firestore, "carts", cartId))
      const cart = snapshot.data()

      if (cart) {
        const inventoryPageIds = Object.keys(cart || {}).filter(
          (_) => _ !== "createdAt"
        )
        setIsCartEmpty(!inventoryPageIds.length)

        setDates(
          cart[inventoryPageId]
            ? cart[inventoryPageId].data.dates.map((_) => new Date(_))
            : []
        )
      } else {
        const docRef = await addDoc(collection(firestore, "carts"), {})
        window.localStorage.setItem("cartId", docRef.id)
      }
    }

    if (cartId) {
      getCart()
    }
  }, [inventoryPageId, cartId, firestore])

  const addToCart = async () => {
    setLoading(true)
    const document = {
      [inventoryPageId]: {
        quantity: dates.length,
        discount:
          dates.length >= MIN_DISCOUNT_DAYS
            ? inventoryPage.stripeDiscount
            : null,
        price: inventoryPage.stripePrice,
        name: inventoryPage.name,
        images: inventoryPage.images.map((img) => img.downloadURL),
        currency,
        data: {
          dates: dates.map((_) => format(new Date(_), "yyyy/MM/dd")),
        },
        taxRates: inventoryPage.taxRates,
      },
      createdAt: serverTimestamp(),
    }

    try {
      await trackEvent(
        "User Tapped Inventory CTA",
        Utils.flattenObject({
          ctaLabel: isCartEmpty ? "Reserve Now" : "Update Cart",
          inventoryPageId,
          name: inventoryPage.name,
          address: inventoryPage.address,
          url,
          partnerName: inventoryPage.partnerName,
          partnerAvatar: (inventoryPage.partnerAvatar || []).map(
            (avatar) => avatar.downloadURL
          ),
          description: inventoryPage.description,
          vanityDescription: inventoryPage.vanityDescription,
          images: document[inventoryPageId].images,
          selectedDates: document[inventoryPageId].data.dates.sort(),
          page: {
            price: formatCurrency(amountInCents, currency),
            quantity: dates.length,
            discountPercentage: formatDiscount(inventoryPage.discount),
            discountAmount: formatCurrency(discount, currency),
            total: formatCurrency(totalInCents, currency),
            subTotal: formatCurrency(totalInCents - discount, currency),
          },
        })
      )
    } catch (error) {
      console.error("Failed to send to Intercom", error)
    }

    if (cartId) {
      await updateDoc(doc(firestore, "carts", cartId), document)
    } else {
      const docRef = await addDoc(collection(firestore, "carts"), document)
      window.localStorage.setItem("cartId", docRef.id)
    }

    setTimeout(() => {
      setLoading(false)
      window.location.href = `${process.env.REACT_APP_FLEXDAY_URL}/cart`
    }, 1500)
  }

  const getFirstAvailableDate = (leadTimeInDays = 0) => {
    let remainingDays = leadTimeInDays
    const startDate = new Date()

    while (remainingDays > 0) {
      startDate.setDate(startDate.getDate() + 1)

      // Exclude weekends (Saturday & Sunday)
      if (startDate.getDay() !== 6 && startDate.getDay() !== 0) {
        remainingDays--
      }
    }

    return startDate
  }

  const fromDate = getFirstAvailableDate(inventory?.leadTimeInDays)

  const getInventoryEventData = () => {
    return Utils.flattenObject({
      inventoryPageId,
      name: inventoryPage.name,
      address: inventoryPage.address,
      url,
      partnerName: inventoryPage.partnerName,
      partnerAvatar: (inventoryPage.partnerAvatar || []).map(
        (avatar) => avatar.downloadURL
      ),
      description: inventoryPage.description,
      vanityDescription: inventoryPage.vanityDescription,
      images: inventoryPage.images.map((image) => image.downloadURL),
    })
  }

  const [inventoryPage] = useDocumentData(
    doc(firestore, "inventoryPages", inventoryPageId)
  )

  const slug = Utils.slugifyName(inventoryPage?.name)
  const url = `${process.env.REACT_APP_FLEXDAY_URL}/suite/${slug}`

  useEffect(async () => {
    if (inventoryPage) {
      try {
        await trackEvent("User Viewed Inventory", getInventoryEventData())
      } catch (error) {
        console.error("Failed to send to Intercom", error)
      }

      setInventory((await getDoc(inventoryPage.inventory)).data())
    }
  }, [inventoryPage])

  // Helper function to check if a date is a weekend (Saturday or Sunday)
  const isWeekend = (date) => {
    const day = date.getDay()
    return day === 0 || day === 6
  }

  // Helper function to check if a date is unavailable
  const isUnavailableDate = (date) => {
    return (inventory.unavailableDates || []).includes(
      format(new Date(date), "yyyy/MM/dd")
    )
  }

  // Helper function to determine if a date should be disabled
  const isDisabled = (date) => {
    return isWeekend(date) || isUnavailableDate(date)
  }

  const onPromoCTA = () => {
    let url = new URL(`${process.env.REACT_APP_FLEXDAY_URL}/book-workspace`)
    url.searchParams.append("selectedlocation", inventoryPage.address)
    url.searchParams.append("selectedSpace", spacesOptions["Flexday Suite"])
    window.location.href = url.href
  }

  if (!inventoryPage) {
    return null
  }

  const isOnDemand = inventoryPage.commitmentTerms.includes("On Demand")
  const amountInCents = isOnDemand
    ? inventoryPage.prices.onDemand[currency.toLowerCase()].amountInCents
    : inventoryPage.prices.longTerm[currency.toLowerCase()].amountInCents

  const totalInCents = amountInCents * dates.length || 0
  const discount =
    dates.length >= MIN_DISCOUNT_DAYS
      ? dates.length * amountInCents * (inventoryPage.discount || 0)
      : 0

  return (
    <div className="flex flex-col items-center gap-4 bg-white p-4 rounded-[20px]">
      <div className="flex w-full justify-stretch items-center gap-2 mb-2">
        <p className="font-bold text-2xl text-center whitespace-nowrap flex-initial w-1/2">
          {formatCurrencyCompact(amountInCents, currency)} <br />
          {isOnDemand && "per day"}
          {!isOnDemand && <span className="text-base">starting per month</span>}
        </p>
        <p className="w-full text-sm text-center flex-auto w-1/2">
          <span className="border-b border-slate-600">
            {inventoryPage.generalLocation}
          </span>
        </p>
      </div>
      {inventoryPage.discount > 0 && (
        <div className="flex justify-center items-center p-2 gap-2">
          <div className="bg-slate-100 p-2 rounded-full">
            <img
              alt="clock"
              src={ClockIcon}
              className="text-slate-400 w-12 opacity-[0.15]"
            />
          </div>
          <p className="text-sm text-center">
            Reserve {MIN_DISCOUNT_DAYS} or more days and get a{" "}
            <strong>{formatDiscount(inventoryPage.discount)} discount</strong>{" "}
            on this fractional office.
          </p>
        </div>
      )}
      {isOnDemand && (
        <>
          <div className="flex flex-col items-center gap-2">
            <DayPicker
              fixedWeeks
              mode="multiple"
              selected={dates}
              onSelect={setDates}
              disabled={isDisabled}
              fromDate={fromDate}
              className="border border-slate-200 p-2 rounded m-0"
            />
            <p className="text-xs text-center">
              Please mark specific dates needed.
            </p>
          </div>
          <p className="text-sm text-center italic">
            <strong>Note:</strong> This room holds up to{" "}
            <b>{inventoryPage.maxCapacity}</b> people.
          </p>
          <div className="flex flex-col items-center gap-2 my-2">
            <Button
              loading={loading}
              title={isCartEmpty ? "Reserve Now" : "Update Cart"}
              className="!w-44 !bg-[#652cb3] rounded-lg
           hover:opacity-75 transition-opacity"
              onClick={addToCart}
              disabled={!dates.length}
            />
            <p className="text-xs text-center">
              Below is an estimate. You won't be charged yet.
            </p>
          </div>
          {totalInCents > 0 && (
            <div className="flex flex-col gap-4 mt-4 w-full">
              <InvoiceItem
                value={totalInCents}
                currency={currency}
                label={`${formatCurrency(amountInCents, currency)} × ${
                  dates.length
                } day${dates.length === 1 ? "" : "s"}`}
              />
              {discount ? (
                <InvoiceItem
                  value={discount}
                  currency={currency}
                  label={`Discount (${formatDiscount(inventoryPage.discount)})`}
                />
              ) : null}
              <InvoiceItem
                boldLabel
                label="Sub-Total *"
                value={`${totalInCents - discount}`}
                currency={currency}
              />
              <p className="text-sm mt-2 italic">* Plus applicable taxes.</p>
            </div>
          )}
          <hr className="h-px bg-slate-200 border-1 w-full" />
        </>
      )}
      <div className="self-stretch flex flex-col gap-1 mt-4">
        <p className="font-bold italic">{inventoryPage.promoHeadline}</p>
        <div className="markdown-container">
          <Markdown remarkPlugins={[remarkGfm]}>
            {inventoryPage.promoDescription}
          </Markdown>
        </div>
        <Button
          title={inventoryPage.promoCta}
          className="self-center !w-32 mt-2 !bg-[#652cb3] rounded-lg
           hover:opacity-75 transition-opacity"
          onClick={onPromoCTA}
        />
      </div>
    </div>
  )
}

export default BookingConfigurator
